import { ReactNode } from 'react';
import './styles/ManagedItemList.scss';

import { Box, Stack } from '@mui/material';
import { Add } from '@mui/icons-material';

import { APP_ICONS } from '@dw/constants/appIcons';
import { componentClassName } from '@dw/utils';

import { Button } from './Button';
import { HStack } from './HStack';
import { IconButton } from './IconButton';

export interface IManagedItemListProps<T> {
  items: T[];
  itemKey: (item: T) => string;
  renderItem: (item: T) => ReactNode;
  onAdd: () => void;
  onEdit: (item: T) => void;
  onDelete: (item: T) => void;
  addLabel: string;
  /** Disables Add and every row's Edit action, and doubles as the tooltip shown on them while disabled. */
  disabledReason?: string;
  /** Per-item reason to disable that row's Delete action (e.g. "still has classes"); return undefined to leave it enabled. Not affected by `disabledReason` - pass that in too if it should also block deletes. */
  deleteDisabledReason?: (item: T) => string | undefined;
}

/**
 * A compact "list of added things" for a modal section: each row shows Edit/Delete, and a single
 * centered Add action sits below the list. That Add action IS the empty state too - when `items` is
 * empty there is no separate "nothing here" placeholder, just the same button a populated list ends
 * with, so a section never needs two different "add a thing" affordances.
 */
export function ManagedItemList<T>(props: IManagedItemListProps<T>) {

  const {
    items,
    itemKey,
    renderItem,
    onAdd,
    onEdit,
    onDelete,
    addLabel,
    disabledReason,
    deleteDisabledReason
  } = props;

  const cn = componentClassName('managed-item-list');

  return (
    <Stack rowGap={1} className={cn()}>
      {items.length > 0 && (
        <Stack rowGap={1}>
          {items.map(item => {
            const deleteReason = deleteDisabledReason?.(item);
            return (
              <HStack key={itemKey(item)} breakpoint={false} columnGap={1} className={cn('row')}>
                <Box flex={1}>{renderItem(item)}</Box>
                <HStack breakpoint={false} columnGap={0.5}>
                  <IconButton
                    icon={APP_ICONS.EDIT}
                    size='small'
                    tooltip={disabledReason ?? 'Edit'}
                    disabled={!!disabledReason}
                    onClick={() => onEdit(item)}
                  />
                  <IconButton
                    icon={APP_ICONS.DELETE}
                    size='small'
                    color='error'
                    tooltip={deleteReason ?? 'Delete'}
                    disabled={!!deleteReason}
                    onClick={() => onDelete(item)}
                  />
                </HStack>
              </HStack>
            );
          })}
        </Stack>
      )}

      <Button
        type='button'
        variant='outlined'
        color='primary'
        fullWidth
        startIcon={<Add />}
        className={cn('add-button')}
        tooltip={disabledReason}
        disabled={!!disabledReason}
        onClick={onAdd}
      >
        {addLabel}
      </Button>
    </Stack>
  );
}
